import { openDB, type DBSchema } from 'idb'

export type OfflinePackStyle = 'standard' | 'outdoor' | 'satellite'

export type OfflinePackMeta = {
  route_id: string
  bbox: [number, number, number, number]
  zoom_min: number
  zoom_max: number
  style: OfflinePackStyle
  tile_count: number
  byte_count: number
  downloaded_at: string
}

interface OfflinePackDb extends DBSchema {
  packs: {
    key: [string, OfflinePackStyle]
    value: OfflinePackMeta
    indexes: {
      route_id: string
    }
  }
}

export const OFFLINE_PACKS_CHANGED_EVENT = 'xr-offline-packs-changed'

const DB_NAME = 'xr-offline'
const DB_VERSION = 1
const STORE_NAME = 'packs'

function getDb() {
  if (!hasIndexedDb()) throw new Error('IndexedDB is not available')
  return openDB<OfflinePackDb>(DB_NAME, DB_VERSION, {
    upgrade(db) {
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: ['route_id', 'style'] })
        store.createIndex('route_id', 'route_id')
      }
    },
  })
}

export function notifyOfflinePacksChanged() {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent(OFFLINE_PACKS_CHANGED_EVENT))
}

export function subscribeOfflinePacksChanged(handler: () => void) {
  if (typeof window === 'undefined') return () => undefined
  window.addEventListener(OFFLINE_PACKS_CHANGED_EVENT, handler)
  return () => window.removeEventListener(OFFLINE_PACKS_CHANGED_EVENT, handler)
}

export async function putOfflinePack(pack: OfflinePackMeta): Promise<void> {
  if (!hasIndexedDb()) return
  const db = await getDb()
  await db.put(STORE_NAME, pack)
  notifyOfflinePacksChanged()
}

export async function listOfflinePacks(): Promise<OfflinePackMeta[]> {
  if (!hasIndexedDb()) return []
  const db = await getDb()
  return db.getAll(STORE_NAME)
}

export async function listOfflinePacksForRoute(routeId: string): Promise<OfflinePackMeta[]> {
  if (!hasIndexedDb()) return []
  const db = await getDb()
  return db.getAllFromIndex(STORE_NAME, 'route_id', routeId)
}

export async function hasOfflinePackForRoute(routeId: string): Promise<boolean> {
  const packs = await listOfflinePacksForRoute(routeId)
  return packs.length > 0
}

export async function deleteOfflinePackMeta(
  routeId: string,
  style: OfflinePackStyle,
): Promise<void> {
  if (!hasIndexedDb()) {
    notifyOfflinePacksChanged()
    return
  }
  const db = await getDb()
  await db.delete(STORE_NAME, [routeId, style])
  notifyOfflinePacksChanged()
}

export async function deleteOfflinePacksForRoute(routeId: string): Promise<void> {
  if (!hasIndexedDb()) {
    notifyOfflinePacksChanged()
    return
  }
  const db = await getDb()
  const tx = db.transaction(STORE_NAME, 'readwrite')
  const keys = await tx.store.index('route_id').getAllKeys(routeId)
  await Promise.all(keys.map((key) => tx.store.delete(key)))
  await tx.done
  notifyOfflinePacksChanged()
}

export async function clearOfflinePackMetas(): Promise<void> {
  if (!hasIndexedDb()) {
    notifyOfflinePacksChanged()
    return
  }
  const db = await getDb()
  await db.clear(STORE_NAME)
  notifyOfflinePacksChanged()
}

function hasIndexedDb(): boolean {
  return typeof indexedDB !== 'undefined' && typeof IDBKeyRange !== 'undefined'
}
